import { ReleaseNotePage } from '../../../shared/models/release-note/release-note.model';

/**
 * The single source for every author-facing cap (RA-2695). The editor's counters and the renderer's list
 * page import these same constants, so the two cannot drift (handoff §6.2). 🔴 Caps are nudges: nothing
 * here ever blocks typing, adding pages, saving or publishing.
 */
export interface Cap {
  /** Over this is amber. */
  soft: number;
  /** Over this is red. */
  hard: number;
}

export const CAPS: { heading: Cap; benefit: Cap; bullet: Cap } = {
  heading: { soft: 40, hard: 60 },
  benefit: { soft: 200, hard: 320 },
  bullet: { soft: 120, hard: 230 }
};

export const BULLETS_WITH_MEDIA = 6;
export const BULLETS_WITHOUT_MEDIA = 12;
export const MAX_PAGES = 8;
export const MAX_MEDIA_PER_PAGE = 3;
/** The renderer goes two-column when a page has no media and more than this many bullets. */
export const LIST_PAGE_BULLET_THRESHOLD = 6;

export const COVER_BUDGET_BYTES = 400_000;
export const STILL_BUDGET_BYTES = 400_000;
export const CLIP_BUDGET_BYTES = 1_500_000;
export const RELEASE_BUDGET_BYTES = 10_000_000;

/** Server ceilings — files over these are refused before any request is made. */
export const STILL_MAX_BYTES = 2_097_152;
export const CLIP_MAX_BYTES = 8_388_608;

/** Stills that may be uploaded — no GIF (motion goes via MP4/WebM). The cover accepts these only. */
export const STILL_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const CLIP_TYPES = ['video/mp4', 'video/webm'];

export type CapSeverity = 'ok' | 'amber' | 'red';

/** The soft bullet cap for a page: tighter when the page carries media. */
export function bulletCapFor(page: Pick<ReleaseNotePage, 'media'>): number {
  return page.media?.length ? BULLETS_WITH_MEDIA : BULLETS_WITHOUT_MEDIA;
}

/** Whether the renderer lays the page out as a two-column list page. */
export function isListPage(page: Pick<ReleaseNotePage, 'media' | 'bullets'>): boolean {
  return !page.media?.length && (page.bullets?.length ?? 0) > LIST_PAGE_BULLET_THRESHOLD;
}

/**
 * Bytes as author-facing megabytes (RA-2870): decimal, to one place — the unit the budgets are written in.
 * The upload-limit message and the weight readout both use it, so the two "MB" figures agree.
 */
export function formatMb(bytes: number): string {
  return (bytes / 1_000_000).toFixed(1);
}

/** How a measured length compares with a cap. */
export function severity(length: number, cap: Cap): CapSeverity {
  if (length > cap.hard) {
    return 'red';
  }
  return length > cap.soft ? 'amber' : 'ok';
}
